import {
  createWaiversInCommonService,
  updateWaiversInCommonService,
  fetchWaiversInCommonService,
  getWaiverDocumentUploadUrl,
  uploadWaiverDocumentToGcs,
  attachWaiverDocumentPaths,
  getWaiverDocumentDownloadUrl,
  mapWaiverLoanId,
} from "../../services/network";
import { submissionFlow } from "../../types/enums";
import { getCookie } from "../../utils/Common";

// Shared by the per-test mitigation/compensating factor dropdown (processor
// request + underwriter action) and the loan-level General Waiver request —
// both use the exact same list, so General Waiver's export is just an alias.
export const MITIGATION_FACTOR_OPTIONS = [
  "Credit Score above minimum by 10 points or greater",
  "Repeat borrower with 2 or more prior loans performing/performed",
  "Experienced borrower with at least 4 rental properties",
  "Reduction in property debt service by 10% or greater",
  "DSCR above min by 0.10x or greater",
  "0X30X24 month mortgage history",
  "LTV/LTC below max by 5% or greater",
  "PITIA reserves above minimum",
  "Other",
];

export const GENERAL_WAIVER_MITIGATION_FACTOR_OPTIONS = MITIGATION_FACTOR_OPTIONS;

export const WaiverStatus = {
  PENDING: "Pending Review",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  APPROVED_CONDITIONALLY: "Approved Conditionally",
} as const;

// The responseType recorded by resetWaiverStatus below — a "reset" is a real
// response entry rather than a locally-cleared one, but it's intentionally
// not in WAIVER_SERVICE_RESPONSE_TYPE/RESPONSE_TYPE_TO_STATUS, so
// getWaiverStatus falls back to WaiverStatus.PENDING for it, same as a
// waiver that's never been touched.
const WAIVER_SERVICE_RE_REQUESTED_RESPONSE_TYPE = "Re-Requested";

// Which statuses represent an underwriter's actual decision (as opposed to
// still awaiting one) — drives whether the compensating-factor/comments
// summary and "decided by" audit line are shown.
export const isWaiverDecided = (status: string): boolean =>
  status === WaiverStatus.APPROVED ||
  status === WaiverStatus.REJECTED ||
  status === WaiverStatus.APPROVED_CONDITIONALLY;

export const MIN_MITIGATION_FACTORS = 2;

export const WAIVER_SERVICE_SOURCE_PLATFORM = "SIZER";

// "EvaluationResult" waivers are keyed off a specific guideline/logic test
// (loanResult.{sizerLoanId}.{testType}.{testKey}); loan-level General Waivers
// (TA-11746) aren't tied to any single test, so they use a placeholder key
// until the waiver-service team defines a real convention for them.
export const GENERAL_WAIVER_TYPE = "GeneralWaiver";
const GENERAL_WAIVER_KEY = "TEMP_GENERAL_WAIVER_KEY";

// Maps our internal WaiverStatus values to the waiver service's responseType
// enum strings, sent as part of the response payload posted to
// /waiverservice/waiver/{waiverId}/response.
export const WAIVER_SERVICE_RESPONSE_TYPE: Record<string, string> = {
  [WaiverStatus.APPROVED]: "APPROVED",
  [WaiverStatus.REJECTED]: "REJECTED",
  [WaiverStatus.APPROVED_CONDITIONALLY]: "APPROVED_CONDITIONALLY",
};

const RESPONSE_TYPE_TO_STATUS: Record<string, string> = Object.fromEntries(
  Object.entries(WAIVER_SERVICE_RESPONSE_TYPE).map(([status, responseType]) => [
    responseType,
    status,
  ]),
);

// The record shape used throughout the waiver UI is the waiver service's own
// object shape (as returned by GET /waiverservice/LOAN/{loanId}/waiver and,
// per the same service, by POST create) — there is no separate local
// translation layer. `waiverResponses` is an append-only history: every
// approve/reject/conditional-approve/reset action adds one entry rather than
// overwriting, so switching outcomes (including a reset) never loses the
// prior decision's audit trail.
export interface WaiverMitigate {
  waiverMitigateId?: number;
  description: string;
}

export interface WaiverServiceResponse {
  responseType?: string;
  responseBy?: string;
  responseDate?: string;
  isApproved?: boolean;
  compensatingFactors?: string[];
  comments?: string;
}

export interface WaiverServiceRecord {
  waiverId?: string;
  waiverType: string;
  waiverKeys: string[];
  title: string;
  description: string;
  takeoutPartnerId?: string | null;
  sizerLoanId?: string;
  sourcePlatform?: string;
  taggedPartyIds?: string[];
  documentPaths: string[];
  waiverMitigates: WaiverMitigate[];
  waiverInfo: {
    hasDocuments?: boolean;
    hasDiscussions?: boolean;
    isInternal?: boolean;
    finalResponse?: string;
    finalCompensatingFactors?: string[] | null;
  };
  waiverResponses: WaiverServiceResponse[];
  createdBy?: string;
  createdOn?: string;
}

// Multi-select form state (mitigationFactor/compensatingFactor) is always a
// plain string[] before it's turned into waiverMitigates — kept for that UI
// layer only; the record itself never carries a bare string.
export const asMitigationFactorArray = (value: unknown): string[] => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value) return [value];
  return [];
};

export const isGeneralWaiverRecord = (waiver: { waiverType?: string }): boolean =>
  waiver?.waiverType === GENERAL_WAIVER_TYPE;

const getWaiverListField = (waiver: { waiverType?: string }) =>
  isGeneralWaiverRecord(waiver) ? "generalWaiverDetails" : "waiverDetails";

// Replaces the matching waiver (by waiverId) in the loan's in-memory waiver
// list and syncs LoansContext, operating purely on the freshest `prev` inside
// the updater — never on a `loanDetails` snapshot passed in by the caller,
// which can go stale within a single synchronous call chain (e.g. creating a
// waiver and then immediately attaching documents to it, before either state
// update has been reflected in a re-render). Falls back to appending when the
// target isn't in the list yet, for that same reason. The waiver service is
// the sole persistence layer for waiver data, so this never calls
// patchSaveApplication.
const replaceWaiverInList = (
  setLoanDetails: ((updater: (prev: any) => any) => void) | undefined,
  updated: WaiverServiceRecord,
): void => {
  const fieldName = getWaiverListField(updated);
  setLoanDetails?.((prev: any) => {
    const list: any[] = prev?.[fieldName] ?? [];
    const exists = list.some((item) => item?.waiverId === updated.waiverId);
    const updatedList = exists
      ? list.map((item) => (item?.waiverId === updated.waiverId ? updated : item))
      : [...list, updated];
    return { ...prev, [fieldName]: updatedList };
  });
};

export const buildTestWaiverKey = (
  loanId: string | undefined,
  testType: string | undefined,
  testKey: string | undefined,
): string => `loanResult.${loanId}.${testType}.${testKey}`;

export const WAIVED_VALUE = "WAIVED";

const parseTestWaiverKey = (
  key: string,
): { loanId: string; testType: string; testKey: string } | null => {
  const match = /^loanResult\.([^.]+)\.([^.]+)\.(.+)$/.exec(key);
  if (!match) return null;
  const [, loanId, testType, testKey] = match;
  return { loanId, testType, testKey };
};

// Derives { [testType]: { [testKey]: "WAIVED" } } from every per-test waiver
// for this loan currently Approved or Approved Conditionally, for merging
// into a rules-evaluation request's resultObject. Computed fresh from
// waiverDetails every time rather than tracked as separate state, so it can
// never drift out of sync with the actual waiver decisions — a
// rejected/reset waiver (status falls back to PENDING) or a General Waiver
// (placeholder key, doesn't match the loanResult.* pattern) is naturally
// excluded rather than needing to be explicitly removed.
export const getOverriddenTestResults = (
  waiverDetails: { waiverKeys?: string[]; waiverResponses?: WaiverServiceResponse[] }[] | undefined,
  loanId: string | undefined,
): Record<string, Record<string, string>> => {
  const overrides: Record<string, Record<string, string>> = {};
  if (!loanId) return overrides;

  (waiverDetails ?? []).forEach((waiver) => {
    const status = getWaiverStatus(waiver);
    if (status !== WaiverStatus.APPROVED && status !== WaiverStatus.APPROVED_CONDITIONALLY) {
      return;
    }

    (waiver.waiverKeys ?? []).forEach((key) => {
      const parsed = parseTestWaiverKey(key);
      if (!parsed || parsed.loanId !== loanId) return;
      overrides[parsed.testType] = {
        ...(overrides[parsed.testType] ?? {}),
        [parsed.testKey]: WAIVED_VALUE,
      };
    });
  });

  return overrides;
};

// Finds the waiver already on file for a specific failed test card, matched
// by the same waiverKeys path the create call built it from — the only
// identifier that reliably survives a round trip through the waiver service
// (unlike, say, the display title, which the service doesn't guarantee comes
// back verbatim).
export const findTestWaiver = (
  waiverDetails: { waiverKeys?: string[] }[] | undefined,
  loanId: string | undefined,
  testType: string | undefined,
  testKey: string | undefined,
): WaiverServiceRecord | undefined => {
  if (!testType || !testKey) return undefined;
  const key = buildTestWaiverKey(loanId, testType, testKey);
  return (waiverDetails ?? []).find((waiver) => waiver?.waiverKeys?.includes(key)) as
    | WaiverServiceRecord
    | undefined;
};

export const getLatestWaiverResponse = (
  waiver: { waiverResponses?: WaiverServiceResponse[] } | undefined,
): WaiverServiceResponse | undefined =>
  waiver?.waiverResponses?.[waiver.waiverResponses.length - 1];

export const getWaiverStatus = (
  waiver: { waiverResponses?: WaiverServiceResponse[] } | undefined,
): string => {
  const responseType = getLatestWaiverResponse(waiver)?.responseType;
  return responseType
    ? RESPONSE_TYPE_TO_STATUS[responseType] ?? WaiverStatus.PENDING
    : WaiverStatus.PENDING;
};

export const getWaiverMitigationDescriptions = (
  waiver: { waiverMitigates?: WaiverMitigate[] } | undefined,
): string[] => (waiver?.waiverMitigates ?? []).map((m) => m?.description).filter(Boolean);

// documentPaths comes back as a bare array of GCS path strings — derive a
// display name from the path's last segment for the UI.
export const getWaiverDocuments = (
  waiver: { documentPaths?: string[] } | undefined,
): { fileName: string; filePath: string }[] =>
  (waiver?.documentPaths ?? []).map((path) => ({
    filePath: path,
    fileName: path?.split("/")?.pop() ?? path,
  }));

// Builds the create-waiver request body for the shared cross-platform waiver
// service (POST /waiverservice/LOAN/{loanId}/waiver). Per-test waivers key off
// the evaluation result path (`loanResult.{sizerLoanId}.{testType}.{testKey}`,
// e.g. `loanResult.123.guidelineTest.maxLeverageArvLtv`); General Waivers use
// a placeholder type/key until that convention is defined. Interest-rate-
// specific fields (interestRate, requestedDiscountPointPercentage/Amount) are
// intentionally omitted — they don't apply to eligibility-test waivers.
// Always created with no documents: the document-upload endpoints require an
// existing waiverId, so any selected files are attached afterward via
// uploadWaiverDocuments once this call has returned one.
export const buildWaiverCreatePayload = (
  loanId: string | undefined,
  assignedPartner: string | undefined,
  waiver: {
    testType?: string;
    testKey?: string;
    testTitle?: string;
    description?: string;
    mitigationFactor?: string[];
    isGeneralWaiver?: boolean;
  },
) => {
  const isGeneralWaiver = !!waiver.isGeneralWaiver;

  return {
    waiverType: isGeneralWaiver ? GENERAL_WAIVER_TYPE : "EvaluationResult",
    waiverKeys: isGeneralWaiver
      ? [GENERAL_WAIVER_KEY]
      : [buildTestWaiverKey(loanId, waiver.testType, waiver.testKey)],
    secondaryIdentifierName: "LOAN_STAGE",
    secondaryIdentifierValue: "FES",
    title: isGeneralWaiver ? "General Waiver Request" : (waiver.testTitle ?? ""),
    description: waiver.description ?? "",
    takeoutPartnerId: assignedPartner ?? null,
    sizerLoanId: loanId,
    sourcePlatform: WAIVER_SERVICE_SOURCE_PLATFORM,
    taggedPartyIds: [],
    waiverMitigates: asMitigationFactorArray(waiver.mitigationFactor).map(
      (description) => ({ description }),
    ),
    documentPaths: [],
    waiverInfo: {
      hasDocuments: false,
      hasDiscussions: false,
      isInternal: false,
      finalResponse: "",
    },
  };
};

// Creates the waiver against the shared waiver service (the sole persistence
// layer — sizer never writes waiver data to its own loan document), then
// merges whatever the service returns over the payload we sent — the create
// response carries the fields sizer can't generate itself (waiverId,
// createdBy, createdOn) — and appends the result to the appropriate local
// list. Returns the merged record so the caller can chain uploadWaiverDocuments
// off the returned waiverId, or pass it straight to onSubmit.
export const persistNewWaiver = async (
  loanId: string | undefined,
  setLoanDetails: ((updater: (prev: any) => any) => void) | undefined,
  waiverRequest: {
    testType?: string;
    testKey?: string;
    testTitle?: string;
    description?: string;
    mitigationFactor?: string[];
    isGeneralWaiver?: boolean;
  },
  assignedPartner?: string,
): Promise<WaiverServiceRecord> => {
  const payload = buildWaiverCreatePayload(loanId, assignedPartner, waiverRequest);
  const response: any = await createWaiversInCommonService(payload, loanId ?? "");
  const record: WaiverServiceRecord = {
    ...payload,
    waiverResponses: [],
    ...(response?.data ?? {}),
  };

  const fieldName = getWaiverListField(record);
  setLoanDetails?.((prev: any) => ({
    ...prev,
    [fieldName]: [...(prev?.[fieldName] ?? []), record],
  }));

  return record;
};

// Uploads each pending file to a presigned per-file GCS URL scoped to this
// waiver (POST .../document/upload-url, then a raw PUT with the Content-Type
// header explicitly cleared — the presigned URL isn't signed with one, so
// sending any value causes a signature mismatch), then records all resulting
// paths on the waiver in a single call (POST .../document). Requires the
// waiver to already exist (waiverId) since the upload-url endpoint is scoped
// to it — call this after persistNewWaiver, not before. No-ops (returning the
// waiver unchanged) when there are no files to upload.
export const uploadWaiverDocuments = async (
  setLoanDetails: ((updater: (prev: any) => any) => void) | undefined,
  waiver: WaiverServiceRecord,
  files: File[],
): Promise<WaiverServiceRecord> => {
  if (!files.length) return waiver;
  if (!waiver?.waiverId) {
    throw new Error("Cannot upload documents: waiver has no waiverId.");
  }

  const paths: string[] = [];
  for (const file of files) {
    const { data } = await getWaiverDocumentUploadUrl(waiver.waiverId, file.name);
    await uploadWaiverDocumentToGcs(file, data?.uploadUrl);
    paths.push(data?.path);
  }
  await attachWaiverDocumentPaths(waiver.waiverId, paths);

  const updatedRecord: WaiverServiceRecord = {
    ...waiver,
    documentPaths: [...(waiver.documentPaths ?? []), ...paths],
  };
  replaceWaiverInList(setLoanDetails, updatedRecord);
  return updatedRecord;
};

// Resolves a presigned, time-limited download URL for a single already-
// attached document (POST .../document/download-url). realmHeaders must be
// passed through explicitly — see getWaiverDocumentDownloadUrl for why.
export const downloadWaiverDocument = async (
  waiverId: string,
  path: string,
  realmHeaders?: any,
): Promise<string | undefined> => {
  const { data } = await getWaiverDocumentDownloadUrl(waiverId, path, realmHeaders);
  return data?.downloadUrl;
};

// Records an underwriter/credit-committee decision (approve/reject/approve-
// conditionally) against the waiver service — the sole persistence layer —
// via POST /waiverservice/waiver/{waiverId}/response, then appends the same
// payload to the record's local waiverResponses history (rather than
// re-fetching) and reflects it in local React state. Throws (rather than
// silently updating local-only state) when the target waiver has no
// waiverId, since there'd be nothing for the recorded decision to be backed
// by remotely.
export const persistWaiverAction = async (
  setLoanDetails: ((updater: (prev: any) => any) => void) | undefined,
  waiver: {
    waiverId?: string;
    waiverType?: string;
    waiverResponses?: WaiverServiceResponse[];
  },
  responsePayload: WaiverServiceResponse,
  setWaiverDecisionVersion?: (updater: (prev: number) => number) => void,
): Promise<void> => {
  if (!waiver?.waiverId) {
    throw new Error("Cannot record this waiver action: missing waiverId.");
  }
  await updateWaiversInCommonService(responsePayload, waiver.waiverId);

  const updatedRecord = {
    ...waiver,
    waiverResponses: [...(waiver.waiverResponses ?? []), responsePayload],
  } as WaiverServiceRecord;
  replaceWaiverInList(setLoanDetails, updatedRecord);
  // Every decision (approve/reject/approve-conditionally, and reset via
  // resetWaiverStatus below) can change getOverriddenTestResults' output —
  // bump the version so the existing debounced evaluation-refresh effects
  // re-fire and the rules engine sees the change.
  setWaiverDecisionVersion?.((prev) => prev + 1);
};

// Used by WaiverSummary's "Reset Waiver" action. Persists the reset as a real
// response entry (responseType "Re-Requested") via the same waiver-service
// response endpoint the approve/reject/conditional actions use, rather than
// just clearing state locally — so it survives a page refresh like any other
// decision. That responseType isn't in RESPONSE_TYPE_TO_STATUS, so
// getWaiverStatus falls back to WaiverStatus.PENDING for it — a reset waiver
// shows as Pending Review, same as one that's never been touched.
// compensatingFactors carries over the waiver's own originally-requested
// mitigation factors (waiverMitigates), rather than going in empty — a reset
// re-opens the waiver for another decision, it doesn't erase why it was
// requested in the first place.
export const resetWaiverStatus = async (
  setLoanDetails: ((updater: (prev: any) => any) => void) | undefined,
  waiver: {
    waiverId?: string;
    waiverType?: string;
    waiverResponses?: WaiverServiceResponse[];
    waiverMitigates?: WaiverMitigate[];
  },
  setWaiverDecisionVersion?: (updater: (prev: number) => number) => void,
): Promise<void> => {
  await persistWaiverAction(
    setLoanDetails,
    waiver,
    {
      responseType: WAIVER_SERVICE_RE_REQUESTED_RESPONSE_TYPE,
      responseBy: getCookie("partyId") ?? "unknown",
      responseDate: new Date().toISOString(),
      isApproved: false,
      compensatingFactors: getWaiverMitigationDescriptions(waiver),
      comments: "",
    },
    setWaiverDecisionVersion,
  );
};

// Populates loanDetails.waiverDetails/generalWaiverDetails straight from the
// shared waiver service's own response shape — the sole persistence layer
// for waivers, so a freshly loaded loan page has nothing else to read
// existing waivers from, and no translation into a separate local shape is
// needed. Fails soft: a waiver-fetch error is logged and otherwise ignored
// rather than blocking the rest of the loan page from loading.
export const fetchAndSyncWaivers = async (
  loanId: string | undefined,
  setLoanDetails: ((updater: (prev: any) => any) => void) | undefined,
  setWaiverDecisionVersion?: (updater: (prev: number) => number) => void,
): Promise<void> => {
  if (!loanId) return;
  try {
    const { data } = await fetchWaiversInCommonService(loanId, loanId);
    const records: WaiverServiceRecord[] = Array.isArray(data) ? data : data?.data ?? [];
    const waiverDetails = records.filter((record) => !isGeneralWaiverRecord(record));
    const generalWaiverDetails = records.filter((record) => isGeneralWaiverRecord(record));

    setLoanDetails?.((prev: any) => ({
      ...prev,
      waiverDetails,
      generalWaiverDetails,
    }));
    // The page's initial rules-evaluation call (fired on mount) can race
    // ahead of this fetch — bumping here guarantees a follow-up evaluation
    // runs with waiverDetails actually populated, so a page refresh always
    // ends up sending the current overrides, not just a waiver decision.
    setWaiverDecisionVersion?.((prev) => prev + 1);
  } catch (err) {
    console.error("Something went wrong while fetching waivers. ERROR:", err);
  }
};

// Which platform-id field name the destination loanId gets registered under
// differs by tenant, keyed off the same ViteAppKcRealm value App.tsx already
// uses to tell tenants apart (see its "bplsizer" check). Any realm not listed
// here falls back to the default (Toorak/merchants) tenant's field name.
const WAIVER_DESTINATION_PLATFORM_ID_NAME_BY_REALM: Record<string, string> = {
  bplsizer: "toorakLoanId",
};
const DEFAULT_WAIVER_DESTINATION_PLATFORM_ID_NAME = "originatorPlatformLoanId";

export const getWaiverDestinationPlatformIdName = (kcRealm: string | undefined, loanSubmissionTo: string | undefined): string => {
  if (loanSubmissionTo === submissionFlow.SUBMIT_TO_TC) {
    return WAIVER_DESTINATION_PLATFORM_ID_NAME_BY_REALM["bplsizer"] ??
      DEFAULT_WAIVER_DESTINATION_PLATFORM_ID_NAME;
  }
  return WAIVER_DESTINATION_PLATFORM_ID_NAME_BY_REALM[kcRealm ?? ""] ??
    DEFAULT_WAIVER_DESTINATION_PLATFORM_ID_NAME;
};
  

// Called once a loan has been submitted to its destination platform (POST
// /waiverservice/waiver/{sizerLoanId}/map) — links the sizer-side loanId used
// throughout the waiver service to whatever loanId the destination platform
// assigned it, so waivers tied to this loan can be traced from either side.
// Fails soft, like fetchAndSyncWaivers: this is a side effect of submission,
// not a precondition for it, so it shouldn't block the user from seeing a
// successful submission if the mapping call itself has trouble.
export const mapWaiverLoanIdToDestination = async (
  sizerLoanId: string | undefined,
  destinationLoanId: string | undefined,
  kcRealm: string | undefined,
  loanSubmissionTo: string | undefined
): Promise<void> => {
  if (!sizerLoanId || !destinationLoanId) return;
  try {
    await mapWaiverLoanId(
      sizerLoanId,
      getWaiverDestinationPlatformIdName(kcRealm, loanSubmissionTo),
      destinationLoanId,
    );
  } catch (err) {
    console.error("Something went wrong while mapping the waiver loan id. ERROR:", err);
  }
};
